import { FORCE_PROF_UI } from '@/constants/common.ts';
import type { Account } from '@/types';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  TUserDetails,
  UserAccountRole,
  UserPermissions,
} from '@/types/account';
import { TProject } from '@/types/projects.ts';

interface TState {
  account: Account | null;
  userDetails: TUserDetails | null;
  defaultProject: TProject | null;
  accountRole: UserAccountRole | null;
  permissions: UserPermissions | null;
  isItMarketer: boolean;
  isItBusiness: boolean;
  resetPasswordEmail: string;
}

const initialState: TState = {
  account: null,
  userDetails: null,
  defaultProject: null,
  accountRole: null,
  permissions: null,
  isItMarketer: false,
  isItBusiness: false,
  resetPasswordEmail: '',
};

type TActions = {
  setDefaultProject: (defaultProject: TProject) => void;
  setAccount: (account: Account) => void;
  setUserDetails: (userDetails: TUserDetails) => void;
  setAccountRole: (accountRole: UserAccountRole | null) => void;
  setPermissions: (permissions: UserPermissions) => void;
  removeAccount: () => void;
  setResetPasswordEmail: (email: string) => void;
};

type TStore = TState & TActions;

export const useAccountStore = create<TStore>()(
  persist(
    (set) => ({
      ...initialState,
      setAccount: (account: Account) => {
        set((state) => ({
          ...state,
          account,
          defaultProject: account?.default_project || null,
          permissions: account?.permissions || null,
          isItMarketer:
            FORCE_PROF_UI ||
            !!account?.permissions?.can_use_rhyme_marketer_service,
          isItBusiness: !!account?.permissions?.can_use_rhyme_business_service,
        }));
      },
      setPermissions: (permissions: UserPermissions) =>
        set((state) => ({
          ...state,
          permissions,
          isItMarketer: !!permissions?.can_use_rhyme_marketer_service,
          isItBusiness: !!permissions?.can_use_rhyme_business_service,
        })),
      setUserDetails: (userDetails) => {
        set((state) => ({
          ...state,
          userDetails,
        }));
      },
      setDefaultProject: (defaultProject) =>
        set((state) => ({ ...state, defaultProject })),
      setAccountRole: (accountRole) =>
        set((state) => ({ ...state, accountRole })),
      setResetPasswordEmail: (resetPasswordEmail) =>
        set((state) => ({ ...state, resetPasswordEmail })),
      removeAccount: () =>
        set((state) => ({
          ...state,
          account: null,
          accountRole: null,
          permissions: null,
          isItMarketer: false,
        })),
    }),
    {
      name: 'account-storage',
    }
  )
);

export default useAccountStore;
